import { useQuery, useMutation } from "react-query"
import toast from "react-hot-toast"

import { apiRequest } from "util/request"
import { queryClient } from "./QueryClientProvider"
import useConfetti from "hooks/useConfetti"

/** ------------------- **/
/*  Integrations
/** ------------------- **/

export function useAuthFacebook() {
  return useMutation(
    ({ code, state }) => {
      state = encodeURI(state)
      return apiRequest(
        `group/facebook_login?code=${code}&state=${state}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success("API token successfully generated")
      },
      onError: error => {
        console.log(error)
        toast.error("Hmm, there was an issue logging in")
      },
    }
  )
}

export function useManualAuthFacebook() {
  const { fireConfetti } = useConfetti()

  return useMutation(
    ({ groupId, fbAdID }) => {
      return apiRequest(
        `group/facebook_login?group_id=${groupId}&fb_aid=${fbAdID}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully received your Facebook advertising ID and will begin importing data"
        )
        fireConfetti()
      },
      onError: error => {
        console.error(error)
        toast.error(
          "Hmm, there was an issue sending your Facebook advertising ID to our servers. Please try again later or contact support."
        )
      },
    }
  )
}

export function useUpdateFacebookUtm() {
  return useMutation(
    ({ groupId, luzManagedUtms }) => {
      return apiRequest(`group/facebook_login?group_id=${groupId}`, "POST", {
        luz_managed_utms: luzManagedUtms,
      })
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully updated your Facebook UTM tag preference"
        )
      },
      onError: error => {
        console.error(error)
        toast.error(
          "Hmm, there was an issue updating your Facebook UTM tag preference. Please try again later or contact support."
        )
      },
    }
  )
}

export function useAuthGoogle() {
  return useMutation(
    ({ code, state }) => {
      state = encodeURI(state)
      return apiRequest(
        `group/google_login?code=${code}&state=${state}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success("API token successfully generated")
      },
      onError: error => {
        console.log(error)
        toast.error("Hmm, there was an issue logging in")
      },
    }
  )
}

export function useInitAuthGoogle(group_id, integration) {
  const cacheKey = ["GoogleInit", group_id, integration]
  const query = () =>
    apiRequest(
      `group/google_login_init?group_id=${group_id}&integration_id=${integration}`,
      "GET"
    )
  return useQuery(cacheKey, query, { enabled: !!group_id })
}

export function useManualAuthGoogleAnalytics() {
  const { fireConfetti } = useConfetti()

  return useMutation(
    ({ groupId, gaViewID }) => {
      return apiRequest(
        `group/google_login?group_id=${groupId}&analytics_vid=${gaViewID}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully received your Google Analytics view ID and will begin importing data"
        )
        fireConfetti()
      },
      onError: error => {
        console.error(error)
        toast.error(
          "Hmm, there was an issue sending your Google Analytics view ID to our servers. Please try again later or contact support."
        )
      },
    }
  )
}

export function useAuthTikTok() {
  return useMutation(
    ({ code, groupId }) => {
      return apiRequest(
        `group/tiktok_login?auth_code=${code}&group_id=${groupId}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success("API token successfully generated")
      },
      onError: error => {
        console.log(error)
        toast.error("Hmm, there was an issue logging in")
      },
    }
  )
}

export function useAuthSlack() {
  return useMutation(
    ({ code, groupId }) => {
      return apiRequest(`group/${groupId}/slack_login`, "POST", {
        code,
      })
    },
    {
      onError: error => {
        console.log(error)
      },
    }
  )
}

export function useUpdateTikTokUtm() {
  return useMutation(
    ({ groupId, luzManagedUtms }) => {
      return apiRequest(`group/tiktok_login?group_id=${groupId}`, "POST", {
        luz_managed_utms: luzManagedUtms,
      })
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully updated your TikTok UTM tag preference"
        )
      },
      onError: error => {
        console.error(error)
        toast.error(
          "Hmm, there was an issue updating your TikTok UTM tag preference. Please try again later or contact support."
        )
      },
    }
  )
}

export function useRevokeIntegrationToken() {
  return useMutation(
    ({ group_id, integration, mode }) => {
      return apiRequest(
        `group/integrations_login_revoke?group_id=${group_id}&integration_id=${integration}&mode=${mode}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success("Integration access successfully revoked")
        queryClient.invalidateQueries(["user"])
      },
      onError: error => {
        console.log(error)
        toast.error("Hmm, there was an issue revoking your login")
      },
    }
  )
}

export function useSetShopifyIntegration() {
  const { fireConfetti } = useConfetti()

  return useMutation(
    ({ groupId, shopName, apiAccessToken }) => {
      return apiRequest(`group/${groupId}/set_shopify_integration`, "POST", {
        dev_store_name: shopName,
        access_token: apiAccessToken,
      })
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully received your shopify integration data"
        )
        fireConfetti()
      },
      onError: error => {
        console.error(error)
        if (error?.message?.includes("Internal server error")) {
          toast.error(
            "Hmm, there was an issue sending your shopify integration data"
          )
        } else if (error?.message) {
          toast.error(error.message)
        } else {
          toast.error(
            "Hmm, there was an issue integrating your shopify data, please try again or contact support"
          )
        }
      },
    }
  )
}

// submit pixel integration for checking
export function useSetPixelIntegration() {
  const { fireConfetti } = useConfetti()

  return useMutation(
    ({ groupId, site }) => {
      return apiRequest(`group/${groupId}/luz_pixel_verification`, "POST", {
        site,
      })
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully received your Luz Pixel integration"
        )
        fireConfetti()
      },
      onError: error => {
        console.error(error)
        toast.error(
          "Hmm, there was an issue with your Luz Pixel integration, please try again"
        )
      },
    }
  )
}

export function useAuthKlaviyo() {
  const { fireConfetti } = useConfetti()

  return useMutation(
    ({ groupId, klavAPIKey }) => {
      return apiRequest(
        `group/klaviyo_login?group_id=${groupId}&klav_api_key=${klavAPIKey}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully received your Klaviyo API key and will begin importing data"
        )
        fireConfetti()
      },
      onError: error => {
        console.error(error)
        if (error.message.includes("Invalid API key")) {
          toast.error("Hmm, we couldn't validate that Klaviyo API key")
        } else if (error.message.includes("InvalidRequestException")) {
          toast.error(
            "Hmm, something went wrong finding a place to store your key, please try again in a few minutes"
          )
        } else {
          toast.error(
            "Hmm, there was an issue sending your Klaviyo API key to our servers. Please try again later or contact support"
          )
        }
      },
    }
  )
}

export function useAuthMailchimp() {
  const { fireConfetti } = useConfetti()

  return useMutation(
    ({ groupId, mailchimpApiKey }) => {
      return apiRequest(
        `group/mailchimp_login?group_id=${groupId}&mailchimp_api_key=${mailchimpApiKey}`,
        "POST"
      )
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully received your Mailchimp API key and will begin importing data"
        )
        fireConfetti()
      },
      onError: error => {
        console.error(error)
        if (error.message.includes("Invalid API key")) {
          toast.error("Hmm, we couldn't validate that Mailchimp API key")
        } else if (error.message.includes("InvalidRequestException")) {
          toast.error(
            "Hmm, looks like Mailchimp thought that API key was invalid, try verifying you entered it correctly"
          )
        } else {
          toast.error(
            "Hmm, there was an issue sending your Mailchimp API key to our servers. Please try again later or contact support"
          )
        }
      },
    }
  )
}

export function useSetDemandPlanningIntegration() {
  const { fireConfetti } = useConfetti()

  return useMutation(
    ({ groupId }) => {
      return apiRequest(`group/${groupId}/verify_upload`, "POST")
    },
    {
      onSuccess: () => {
        toast.success(
          "We have successfully received your shopify integration data"
        )
        fireConfetti()
      },
      onError: error => {
        console.error(error)
        if (error.message.includes("Internal server error")) {
          toast.error(
            "Hmm, something went wrong on our end uploading your file, please contact support"
          )
        } else if (error.message) {
          toast.error(error.message)
        } else {
          toast.error(
            "Hmm, there was an issue uploading your file, please try again or contact support"
          )
        }
      },
    }
  )
}
